'use client';

import { useEffect, useRef, useState } from 'react';
import { animate, useInView, useReducedMotion } from 'framer-motion';

interface CountUpLabelProps {
  /** Full label, e.g. "100% control in your smartphone" or "3 years". */
  label: string;
  /** Count-up duration in seconds. */
  duration?: number;
}

/**
 * Renders a label whose leading integer counts up from 0 to its value the first
 * time it scrolls into view (e.g. "100% control…" animates 0 → 100, "3 years"
 * does 0 → 3). Labels with no leading number render unchanged. Honors
 * prefers-reduced-motion by showing the final value immediately.
 *
 * SSR contract: server HTML (and the first client render, so hydration
 * matches) must contain the REAL number, not 0 — crawlers and no-JS renders
 * read this text. The 0 → target animation is a client-only effect that
 * starts after the badge scrolls into view.
 */
export default function CountUpLabel({ label, duration = 1.1 }: CountUpLabelProps) {
  const match = label.match(/^(\d+)(.*)$/);
  const hasNumber = match !== null;
  const target = match ? parseInt(match[1], 10) : 0;
  const rest = match ? match[2] : '';

  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-15% 0px' });
  const reduce = useReducedMotion();
  const [value, setValue] = useState(target);

  useEffect(() => {
    if (!hasNumber) return;
    if (reduce) {
      setValue(target);
      return;
    }
    if (!inView) return;
    const controls = animate(0, target, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setValue(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, reduce, hasNumber, target, duration]);

  if (!hasNumber) return <>{label}</>;

  return (
    <span ref={ref}>
      {/* tabular-nums keeps the digits from jittering as the count climbs */}
      <span className="tabular-nums">{value}</span>
      {rest}
    </span>
  );
}
